import { collection, addDoc, serverTimestamp, query, where, getDocs } from 'firebase/firestore';
import { db, auth } from '../config/firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

/**
 * Service pour gérer l'envoi d'emails via Resend
 */
export const emailService = {
  /**
   * Envoie un email via Firebase Cloud Function
   */
  sendEmailViaCloudFunction: async (to: string | string[], subject: string, text: string, html: string, from?: string): Promise<void> => {
    try {
      const functions = getFunctions();
      const sendEmailFunction = httpsCallable(functions, 'sendEmail');

      const result = await sendEmailFunction({
        to,
        subject,
        text,
        html,
        from,
      });

      console.log('E-mail envoyé avec succès:', result.data);
    } catch (error) {
      console.error('Erreur lors de l\'envoi de l\'e-mail:', error);
      throw error;
    }
  },
  /**
   * Planifie l'envoi d'un email de rappel de rendez-vous
   * @param appointment Les données du rendez-vous
   * @param alertDate La date d'envoi de l'alerte
   * @returns Promise<void>
   */
  scheduleAppointmentReminder: async (appointment: any, alertDate: Date): Promise<void> => {
    if (!auth.currentUser || !auth.currentUser.email) {
      throw new Error('Utilisateur non connecté ou email manquant');
    }

    const appointmentDate = new Date(appointment.date);

    const htmlContent = `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <h2 style="color: #2563eb;">Rappel de rendez-vous</h2>
        <p>Bonjour,</p>
        <p>Ceci est un rappel pour votre rendez-vous :</p>
        <ul style="list-style: none; padding: 0;">
          <li style="padding: 8px 0;"><strong>Entreprise:</strong> ${appointment.title}</li>
          <li style="padding: 8px 0;"><strong>Contact:</strong> ${appointment.contact}</li>
          <li style="padding: 8px 0;"><strong>Date:</strong> ${format(appointmentDate, 'dd/MM/yyyy', { locale: fr })}</li>
          <li style="padding: 8px 0;"><strong>Heure:</strong> ${appointment.time}</li>
          <li style="padding: 8px 0;"><strong>Agence:</strong> ${appointment.agency}</li>
        </ul>
        <p>Cordialement,<br><strong>L'équipe Anthea RH</strong></p>
      </div>
    `;

    const textContent = `Rappel: Vous avez un rendez-vous avec ${appointment.contact} de ${appointment.title} le ${format(appointmentDate, 'dd/MM/yyyy', { locale: fr })} à ${appointment.time}.`;

    // Si la date d'alerte est maintenant ou passée, envoyer immédiatement
    const now = new Date();
    if (alertDate <= now) {
      await emailService.sendEmailViaCloudFunction(
        auth.currentUser.email,
        `Rappel: Rendez-vous avec ${appointment.title}`,
        textContent,
        htmlContent
      );
    } else {
      // Sinon, créer un document dans la collection emailQueue pour envoi différé
      await addDoc(collection(db, 'emailQueue'), {
        to: auth.currentUser.email,
        subject: `Rappel: Rendez-vous avec ${appointment.title}`,
        text: textContent,
        html: htmlContent,
        scheduledFor: alertDate,
        status: 'pending',
        appointmentId: appointment.id,
        createdAt: serverTimestamp()
      });
    }
  },

  /**
   * Envoie un email immédiatement
   * @param to Adresse email du destinataire
   * @param subject Sujet de l'email
   * @param text Contenu texte de l'email
   * @param html Contenu HTML de l'email
   * @returns Promise<void>
   */
  sendEmail: async (to: string, subject: string, text: string, html: string): Promise<void> => {
    await emailService.sendEmailViaCloudFunction(to, subject, text, html);
  },

  /**
   * Envoie des notifications par email pour les rendez-vous en retard
   * @param email Adresse email du destinataire (par défaut l'utilisateur connecté)
   * @returns Promise<void>
   */
  sendLateAppointmentsNotification: async (email?: string): Promise<void> => {
    if (!auth.currentUser) {
      throw new Error('Utilisateur non connecté');
    }

    // Utiliser l'email de l'utilisateur connecté si aucun email n'est fourni
    const userEmail = email || auth.currentUser.email;
    if (!userEmail) {
      throw new Error('Email manquant');
    }

    try {
      // Récupérer les alertes en retard
      const alertsQuery = query(
        collection(db, 'alerts'),
        where('userId', '==', auth.currentUser.uid),
        where('status', '==', 'late')
      );

      const querySnapshot = await getDocs(alertsQuery);

      if (querySnapshot.empty) {
        console.log('Aucune alerte en retard');
        return;
      }

      // Préparer le contenu de l'email
      const lateAlerts = querySnapshot.docs.map(doc => doc.data());

      let htmlContent = `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <h2 style="color: #dc2626;">Alertes de rendez-vous en retard</h2>
          <p>Bonjour,</p>
          <p>Voici la liste des rendez-vous en retard :</p>
          <ul style="list-style: none; padding: 0;">
      `;

      let textContent = `Alertes de rendez-vous en retard\n\n`;

      for (const alert of lateAlerts) {
        const alertDate = alert.date.toDate();

        htmlContent += `
          <li style="padding: 12px; margin: 8px 0; border-left: 4px solid #dc2626; background-color: #fef2f2;">
            <strong style="color: #dc2626;">${alert.company}</strong> -
            ${format(alertDate, 'dd/MM/yyyy', { locale: fr })}
            <p style="margin: 4px 0 0 0;">${alert.description}</p>
          </li>
        `;

        textContent += `- ${alert.company} - ${format(alertDate, 'dd/MM/yyyy', { locale: fr })}\n  ${alert.description}\n\n`;
      }

      htmlContent += `
          </ul>
          <p>Cordialement,<br><strong>L'équipe Anthea RH</strong></p>
        </div>
      `;

      textContent += `Cordialement,\nL'équipe Anthea RH`;

      // Envoyer l'email immédiatement
      await emailService.sendEmailViaCloudFunction(
        userEmail,
        `${lateAlerts.length} rendez-vous en retard - Anthea RH`,
        textContent,
        htmlContent
      );

      console.log(`Email de notification envoyé à ${userEmail}`);
    } catch (error) {
      console.error('Error sending late appointments notification:', error);
      throw error;
    }
  }
};